import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {useFirebaseAnalytics} from "@/hooks/useFirebaseAnalytics";
import { useDownloadUrls } from "@/hooks/useDownloadUrls";

const balanceItems = [
  { labelKey: "hero_usdBalance", amount: "12,450.00", change: "+$320", positive: true },
  { labelKey: "hero_kztBalance", amount: "5,780,000", change: "+₸12,500", positive: true },
];

const transactions = [
  { icon: "ri-arrow-up-line", labelKey: "hero_tx1", amount: "-$250.00", timeKey: "hero_minAgo", positive: false },
  { icon: "ri-arrow-down-line", labelKey: "hero_tx2", amount: "+$1,200.00", timeKey: "hero_hrAgo", positive: true },
  { icon: "ri-arrow-down-line", labelKey: "hero_tx4", amount: "+$430.00", timeKey: "hero_hrsAgo", positive: true },
];

const quickActions = [
  { icon: "ri-add-line", key: "hero_topUp" },
  { icon: "ri-send-plane-line", key: "hero_send" },
  { icon: "ri-exchange-line", key: "hero_exchange" },
  { icon: "ri-history-line", key: "hero_history" },
];

export default function HeroSection() {
  const { t } = useTranslation();
  const [activeBalance, setActiveBalance] = useState(0);
  const [mounted, setMounted] = useState(false);

  const { appStoreDownloadUrl, apkDownloadUrl } = useDownloadUrls();

  const {
    logEventDownloadAppStore,
    logEventDownloadApk
  } = useFirebaseAnalytics();

  useEffect(() => {
    // следующий кадр, а не тело эффекта: иначе анимация входа не успевает проиграться
    const raf = requestAnimationFrame(() => setMounted(true));
    const interval = setInterval(() => {
      setActiveBalance((prev) => (prev + 1) % balanceItems.length);
    }, 2800);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(interval);
    };
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center pt-16 overflow-hidden bg-white"
    >
      <div
        className="absolute right-0 top-0 w-[55%] h-full pointer-events-none"
        style={{ background: "radial-gradient(ellipse 70% 80% at 80% 40%, #dbeafe 0%, transparent 70%)" }}
      />
      <div
        className="absolute left-0 bottom-0 w-[40%] h-[50%] pointer-events-none"
        style={{ background: "radial-gradient(ellipse 60% 60% at 10% 90%, #eff6ff 0%, transparent 70%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 w-full py-12">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Left: Copy */}
          <div className={`transition-all duration-1000 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-2 bg-[#007AFF]/10 border border-[#007AFF]/20 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#007AFF] animate-pulse" />
              <span className="text-[#007AFF] text-xs font-semibold tracking-wide uppercase">{t("hero_badge")}</span>
            </div>

            <h1 className="text-5xl lg:text-[3.6rem] font-bold leading-tight tracking-tight text-gray-900 mb-6">
              BroMoney —{" "}
              <span className="text-[#007AFF]">{t("hero_titleAccent")}</span>
              <br />
              {t("hero_title2")}
            </h1>

            <p className="text-gray-500 text-lg leading-relaxed mb-10 max-w-md">
              {t("hero_subtitle")}
            </p>

            <div className="flex flex-wrap gap-3 mb-12">
              <a
                href={appStoreDownloadUrl}
                onClick={() => logEventDownloadAppStore('button_top')}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white border border-gray-200 hover:border-gray-400 text-gray-700 font-semibold px-7 py-3.5 rounded-full cursor-pointer whitespace-nowrap transition-all duration-200 text-base"
              >
                <i className="ri-apple-fill text-gray-700" />
                App Store
              </a>
              <a
                href={apkDownloadUrl}
                onClick={() => logEventDownloadApk('button_top')}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white border border-gray-200 hover:border-gray-400 text-gray-700 font-semibold px-7 py-3.5 rounded-full cursor-pointer whitespace-nowrap transition-all duration-200 text-base"
              >
                <i className="ri-android-line text-gray-700" />
                Android APK
              </a>
            </div>

            <div className="flex items-center gap-10">
              <div>
                <div className="text-2xl font-bold text-gray-900">5К+</div>
                <div className="text-xs text-gray-400 mt-0.5">{t("hero_activeUsers")}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">10+</div>
                <div className="text-xs text-gray-400 mt-0.5">{t("hero_services")}</div>
              </div>
              <div>
                <div className="flex items-center gap-1">
                  <span className="text-2xl font-bold text-gray-900">4.9</span>
                  <i className="ri-star-fill text-amber-400 text-lg" />
                </div>
                <div className="text-xs text-gray-400 mt-0.5">{t("hero_appRating")}</div>
              </div>
            </div>
          </div>

          {/* Right: Card UI Mockup */}
          <div className={`flex justify-center lg:justify-end transition-all duration-1000 delay-300 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative w-full max-w-sm">
              {/* Main card */}
              <div className="relative card-light rounded-2xl p-6 accent-glow mb-4">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0">
                      <img
                        src="/logo.svg"
                        alt="BroMoney"
                        className="w-full h-full object-cover object-left"
                      />
                    </div>
                    <span className="text-gray-800 font-semibold text-sm">BroMoney</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-[#007AFF]/30 rounded-full" />
                    <span className="w-2 h-2 bg-[#007AFF] rounded-full" />
                  </div>
                </div>

                <div className="mb-4">
                  <div className="text-gray-400 text-xs uppercase tracking-widest mb-1">
                    {t(balanceItems[activeBalance].labelKey)}
                  </div>
                  <div className="text-4xl font-bold text-gray-900 tracking-tight">
                    {balanceItems[activeBalance].amount}
                  </div>
                  <div className={`text-sm font-medium mt-1 ${balanceItems[activeBalance].positive ? "text-[#007AFF]" : "text-red-500"}`}>
                    {balanceItems[activeBalance].change} {t("hero_today")}
                  </div>
                </div>

                <div className="flex gap-1.5 mb-6">
                  {balanceItems.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveBalance(i)}
                      className={`h-1 rounded-full transition-all duration-300 cursor-pointer ${i === activeBalance ? "w-6 bg-[#007AFF]" : "w-2 bg-gray-200"}`}
                    />
                  ))}
                </div>

                <div className="grid grid-cols-4 gap-2">
                  {quickActions.map(({ icon, key }) => (
                    <button key={key} className="flex flex-col items-center gap-1.5 cursor-pointer group">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#eff6ff] border border-[#c7dcff] rounded-xl group-hover:bg-[#007AFF]/10 group-hover:border-[#007AFF]/30 transition-all duration-200">
                        <i className={`${icon} text-[#007AFF] text-base`} />
                      </div>
                      <span className="text-gray-400 text-xs">{t(key)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Transactions card */}
              <div className="card-light rounded-2xl p-5 shadow-sm">
                <div className="text-xs text-gray-400 uppercase tracking-widest mb-4">{t("hero_recentActivity")}</div>
                <div className="flex flex-col gap-3">
                  {transactions.map((tx) => (
                    <div key={tx.labelKey} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${tx.positive ? "bg-[#ebf3ff]" : "bg-red-50"}`}>
                          <i className={`${tx.icon} text-sm ${tx.positive ? "text-[#007AFF]" : "text-red-500"}`} />
                        </div>
                        <div>
                          <div className="text-gray-800 text-xs font-medium">{t(tx.labelKey)}</div>
                          <div className="text-gray-400 text-xs">{t(tx.timeKey)}</div>
                        </div>
                      </div>
                      <span className={`text-sm font-semibold ${tx.positive ? "text-[#007AFF]" : "text-gray-700"}`}>{tx.amount}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -right-4 top-16 card-light rounded-xl px-4 py-2.5 accent-glow-sm border border-[#c7dcff]">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-fill text-[#007AFF] text-sm" />
                  <span className="text-gray-700 text-xs font-semibold">{t("hero_verifiedSecure")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
